import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, KeyRound, Loader2, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import { deviceOtpApi, OTP_REASON_LABEL, OTP_SYNC_META } from '../../../../services/deviceOtpApi'
import { formatDateTime } from '../../../../utils/format'

/**
 * Login OTP direto no equipamento: o usuário entra com `nome.sobrenome` + código atual do autenticador
 * da plataforma. Ligar instala o validador (PAM/sshd) no host e mantém as contas de quem tem direito
 * (Owner/Admin do tenant ou global, com autenticador ligado) sincronizadas.
 */
export default function OtpLoginSection({ device }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)

  const { data: status, isLoading } = useQuery({
    queryKey: ['deviceOtp', device.id],
    queryFn: () => deviceOtpApi.status(device.id),
    refetchInterval: (q) => (['pending', 'removing'].includes(q.state.data?.syncStatus) ? 5000 : 30000),
  })
  const { data: me } = useQuery({ queryKey: ['deviceOtpMe'], queryFn: deviceOtpApi.me, staleTime: 60000 })

  const run = async (fn) => {
    setBusy(true)
    setMessage(null)
    try {
      queryClient.setQueryData(['deviceOtp', device.id], await fn())
    } catch (e) {
      setMessage(e.message)
    } finally {
      setBusy(false)
    }
  }

  const meta = OTP_SYNC_META[status?.syncStatus] || OTP_SYNC_META.off
  const isRouter = device.kindCategory === 'Router'

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-theme-primary" />
        <h3 className="text-sm font-semibold text-gray-900">Login OTP</h3>
        {status?.supported && (
          <span className={clsx('px-2 py-0.5 text-xs rounded-full', meta.cls)}>{meta.label}</span>
        )}
        {status?.supported && (
          <div className="ml-auto flex gap-2">
            {status.enabled && (
              <button
                onClick={() => run(() => deviceOtpApi.syncNow(device.id))}
                disabled={busy}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className="w-3 h-3" /> Sincronizar
              </button>
            )}
            <button
              onClick={() => run(() => deviceOtpApi.setEnabled(device.id, !status.enabled))}
              disabled={busy}
              className={clsx(
                'inline-flex items-center gap-1 text-xs px-2 py-1 rounded disabled:opacity-50',
                status.enabled
                  ? 'border border-red-300 text-red-700 hover:bg-red-50'
                  : 'bg-theme-primary text-white hover:opacity-90'
              )}
            >
              {busy && <Loader2 className="w-3 h-3 animate-spin" />}
              {status.enabled ? 'Desligar' : 'Ligar'}
            </button>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Entre direto no equipamento ({isRouter ? 'Winbox' : 'SSH'}) com seu login{' '}
        <span className="font-mono">nome.sobrenome</span> e o código atual do seu autenticador, sem senha. A nuvem
        valida cada login e cada código vale uma única vez. Têm acesso os Owner/Admin do tenant com autenticador
        ligado; quem perde o acesso é barrado na hora.
        {isRouter && ' Ligar configura o MikroTik (RADIUS da plataforma, /user aaa e Winbox liberado só pela VPN).'}
      </p>

      {me && (
        <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
          {me.mfaEnabled ? (
            <>
              Seu login: <span className="font-mono font-semibold">{me.loginName}</span>
            </>
          ) : (
            <>Ative o autenticador em Configurações › Segurança para usar o login OTP.</>
          )}
        </div>
      )}

      {message && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{message}</div>
      )}

      {isLoading && <Loader2 className="w-4 h-4 animate-spin text-theme-primary" />}

      {status && !status.supported && <p className="text-xs text-gray-500">{status.unsupportedReason}</p>}

      {status?.supported && (
        <div className="space-y-3">
          {status.lastSyncError && (
            <div className="flex gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="break-all">{status.lastSyncError}</span>
            </div>
          )}
          {status.report?.length > 0 && (
            <ul className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 space-y-0.5">
              {status.report.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          {(status.appliedAt || status.lastSyncAttemptAt) && (
            <p className="text-xs text-gray-400">
              {status.appliedAt && <>Aplicado em {formatDateTime(status.appliedAt)}. </>}
              {status.lastSyncAttemptAt && <>Última tentativa {formatDateTime(status.lastSyncAttemptAt)}.</>}
            </p>
          )}

          <div>
            <div className="text-xs font-medium text-gray-700 mb-1">Quem pode entrar</div>
            {status.accounts.length === 0 ? (
              <p className="text-xs text-gray-500">Ninguém: nenhum Owner/Admin com autenticador ligado.</p>
            ) : (
              <table className="w-full text-xs">
                <tbody className="divide-y divide-gray-100">
                  {status.accounts.map((a) => (
                    <tr key={a.userId}>
                      <td className="py-1 text-gray-700">{a.name}</td>
                      <td className="py-1 font-mono text-gray-900">{a.loginName}</td>
                      <td className="py-1 text-right">
                        {a.conflict && (
                          <span
                            className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700"
                            title="Já existe uma conta local com este nome que não é da plataforma; ela não foi alterada."
                          >
                            conta local com o mesmo nome (prevalece)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {status.recentLogins?.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">Últimos logins</div>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-gray-100">
                  {status.recentLogins.map((l, i) => (
                    <tr key={i}>
                      <td className="py-1 text-gray-500 whitespace-nowrap">{formatDateTime(l.createdAt)}</td>
                      <td className="py-1 font-mono text-gray-900">{l.username}</td>
                      <td className={clsx('py-1', l.success ? 'text-green-700' : 'text-red-700')}>
                        {l.success ? 'entrou' : OTP_REASON_LABEL[l.reason] || l.reason}
                      </td>
                      <td className="py-1 text-right font-mono text-gray-400">{l.remoteHost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
